// PhotoPipeline — capture-only orchestrator. Mounts MemoryBookCapture
// and, when Haiku returns a row, hands the user off to ScanPipeline's
// review surface so every "we identified an item" path lands in the
// SAME place. The bespoke chip-card review that used to live here was
// retired (it diverged from the canonical scan-review register, which
// caused the photo path to feel cold and the "Take a photo" button to
// loop on already-bound rows).
//
// Three branches:
//   * canonical bound on capture-complete → populate the store, switch
//     to "scan" mode. ScanPipeline mounts at phase="review" because
//     draft.canonicalId is set, lands on the unified review surface.
//   * proposal only (Haiku had a name suggestion but no registry hit)
//     → render ProposalCard. On confirm, mint the synthetic, populate,
//     hand off to scan. On dismiss, return to entry gate.
//   * mounted with canonical already bound → user came back through
//     the EntryGate "Photo" tile after the canonical was set somewhere
//     else. Hand off immediately; no reason to re-photograph.

import { useState } from "react";
import { useDraftStore } from "./store";
import { canonicalAutofillSync } from "../../../lib/canonicalAutofill";
import { findIngredient } from "../../../data/ingredients";
import { useIngredientInfo } from "../../../lib/useIngredientInfo";
import { useAuth } from "../../../lib/useAuth";
import { createPendingCanonicalFromScan } from "../../../lib/createPendingCanonicalFromScan";
import MemoryBookCapture from "../MemoryBookCapture";
import { ProposalCard } from "./ScanReview";

export default function PhotoPipeline({ store, onSwitchPipeline }) {
  const draft = useDraftStore(store, (s) => s);
  const { getInfo } = useIngredientInfo();
  const { userId } = useAuth();
  const [phase, setPhase] = useState("capture");

  // Always start at capture. The user opted into Photo on purpose;
  // even when a canonical is already bound from a prior step, the
  // photo's job is to fill the OTHER axes (brand, state, size) by
  // reading the package. The earlier "redirect to scan when bound"
  // shortcut prevented the camera from ever appearing on already-
  // resolved rows, which the user flagged.

  function applyDraftRow(row) {
    if (!row) return;
    if (row.barcodeUpc) store.setMeta({ barcodeUpc: row.barcodeUpc });
    if (row.canonicalId) {
      const ing = findIngredient(row.canonicalId);
      store.setCanonical(
        row.canonicalId,
        ing?.name || row.name || row.canonicalId,
        ing?.emoji || null,
        "off",
      );
    }
    if (row.brand)         store.setAxis("brand",         row.brand,         "off");
    if (row.variant)       store.setAxis("variant",       row.variant,       "off");
    if (row.typeId)        store.setAxis("typeId",        row.typeId,        "off");
    if (row.tileId)        store.setAxis("tileId",        row.tileId,        "off");
    if (row.state)         store.setAxis("state",         row.state,         "off");
    if (row.cut)           store.setAxis("cut",           row.cut,           "off");
    if (row.packageAmount) store.setAxis("packageAmount", row.packageAmount, "off");
    if (row.packageUnit)   store.setAxis("packageUnit",   row.packageUnit,   "off");
    store.setMeta({
      nutrition:         row.nutrition         || null,
      ingredientsText:   row.ingredientsText   || null,
      claims:            row.claims            || null,
      barcodeMetadata:   row.learnedCorrection?.productMetadata || null,
      proposedCanonical: row.proposedCanonical || null,
    });

    if (row.canonicalId) {
      const dbInfo = getInfo(row.canonicalId);
      const fill = canonicalAutofillSync(row.canonicalId, {
        brand: row.brand || null,
        dbInfo,
        name:  row.name  || null,
      });
      store.applyAutofill(fill);
      // Bound — hand off to ScanPipeline's review surface.
      onSwitchPipeline?.("scan");
      return;
    }

    if (row.proposedCanonical) {
      // No bind, but Haiku has a name suggestion. Mount the proposal
      // card locally before handing off.
      setPhase("proposal");
      return;
    }

    // Nothing extracted — back to entry gate.
    onSwitchPipeline?.("entry");
  }

  async function confirmProposal(editedName) {
    const proposal = draft.proposedCanonical;
    if (!proposal || !userId) return;
    const finalName = (editedName || proposal.suggestedName || "").trim();
    if (!finalName) return;
    try {
      const synth = await createPendingCanonicalFromScan({
        userId,
        productName:   finalName,
        brand:         proposal.brand         || null,
        categoryHints: proposal.categoryHints || null,
      });
      if (synth?.canonicalId) {
        const ing = findIngredient(synth.canonicalId);
        store.setCanonical(
          synth.canonicalId,
          ing?.name || finalName,
          ing?.emoji || null,
          synth.source || "scan-created-pending",
        );
        store.setMeta({ proposedCanonical: null });
        onSwitchPipeline?.("scan");
      }
    } catch (e) {
      console.warn("[photo] proposal confirm failed:", e?.message || e);
    }
  }

  function dismissProposal() {
    store.setMeta({ proposedCanonical: null });
    onSwitchPipeline?.("entry");
  }

  if (phase === "capture") {
    return (
      <MemoryBookCapture
        barcodeUpc={draft.barcodeUpc || null}
        onComplete={applyDraftRow}
        onCancel={() => onSwitchPipeline?.("entry")}
      />
    );
  }

  if (phase === "proposal" && draft.proposedCanonical) {
    return (
      <ProposalCard
        proposal={draft.proposedCanonical}
        onConfirm={confirmProposal}
        onDismiss={dismissProposal}
      />
    );
  }

  return null;
}
